// TODO: should be marked as server-only
// import 'server-only';
import { Locale } from '@headless-workspace/config';
import { Result, ResultEmpty, ResultSuccess, unwrapResultSuccess } from '@headless-workspace/core-domain';
import {
    AppLinksBannerRepositoryType,
    BookingConfigRepositoryType,
    CommonDI,
    CrossBorderRepositoryType,
    CrossSellsBundlesConfigRepositoryType,
    DeliveryToStoreRepositoryType,
    FreeShippingPriceRepositoryType,
    LocalitiesConfigRepositoryType,
    LoyaltyConfigsRepositoryType,
    MiniSizeConfigRepositoryType,
    ModerationTermsRepositoryType,
    NewsletterConfigRepositoryType,
    NotifyMeRepositoryType,
    OmnibusInformationsRepositoryType,
    PriceLegalMentionsRepositoryType,
    PromoStandardTextRepositoryType,
    RedDotRepositoryType,
    Target2SellConfigRepositoryType,
    VipBrandsRepositoryType,
} from '../../../DI';
import { EcomConfigKeys } from './EcomConfigKeys';

const getEcomConfigs = (locale: Locale) => ({
    [EcomConfigKeys.EnableNewsletterBoostOptInOffer]: () =>
        CommonDI.get(NewsletterConfigRepositoryType).fetchEnableNewsletterBoostOptinOfferConfiguration(locale),
    [EcomConfigKeys.MiniSizeConfig]: () => CommonDI.get(MiniSizeConfigRepositoryType).getMiniSizeConfig(locale),
    [EcomConfigKeys.SmartBannerKey]: () => CommonDI.get(AppLinksBannerRepositoryType).fetchConfiguration(locale),
    [EcomConfigKeys.BooxiApiKey]: () => CommonDI.get(BookingConfigRepositoryType).fetchBookingConfiguration(locale),
    [EcomConfigKeys.WoosmapApiKey]: () =>
        CommonDI.get(LocalitiesConfigRepositoryType).fetchLocalitiesConfiguration(locale),
    [EcomConfigKeys.LoyaltyConfig]: () => CommonDI.get(LoyaltyConfigsRepositoryType).fetchLoyaltyConfiguration(locale),
    [EcomConfigKeys.ProductPriceShippingThreshold]: () =>
        CommonDI.get(FreeShippingPriceRepositoryType).fetchFreeShippingPrice(locale),
    [EcomConfigKeys.EnablePriceLegalMentions]: () =>
        CommonDI.get(PriceLegalMentionsRepositoryType).fetchEnablePriceLegalMentions(locale),
    [EcomConfigKeys.VipBrands]: () => CommonDI.get(VipBrandsRepositoryType).fetchVipBrands(locale),
    [EcomConfigKeys.EnableCrossBorder]: () => CommonDI.get(CrossBorderRepositoryType).getIsCrossBorder(locale),
    [EcomConfigKeys.EnableCrossSellsBundles]: () =>
        CommonDI.get(CrossSellsBundlesConfigRepositoryType).isEnabled(locale),
    [EcomConfigKeys.EnableDeliveryToStore]: () =>
        CommonDI.get(DeliveryToStoreRepositoryType).getIsDeliveryToStoreEnabled(locale),
    [EcomConfigKeys.ModerationList]: () => CommonDI.get(ModerationTermsRepositoryType).getModerationTerms(locale),
    [EcomConfigKeys.EnableNotifyMe]: () => CommonDI.get(NotifyMeRepositoryType).getIsNotifyMeEnabled(locale),
    [EcomConfigKeys.NotifyMeDisabledCountries]: () =>
        CommonDI.get(NotifyMeRepositoryType).getIsNotifyMeDisabledForCountry(locale),
    [EcomConfigKeys.RedDotConfig]: () => CommonDI.get(RedDotRepositoryType).fetchRedDotConfig(locale),
    [EcomConfigKeys.T2SConfig]: () => CommonDI.get(Target2SellConfigRepositoryType).fetchTarget2SellConfig(locale),
    [EcomConfigKeys.OmnibusInformation]: () =>
        CommonDI.get(OmnibusInformationsRepositoryType).getOmnibusInformations(locale),
    [EcomConfigKeys.PromoStandardText]: () =>
        CommonDI.get(PromoStandardTextRepositoryType).getPromoStandardText(locale),
});

type EcomConfigsFunctions = ReturnType<typeof getEcomConfigs>;
type UnwrapResult<T> = T extends Promise<Result<infer U>> ? U : T extends Result<infer U> ? U : never;
export type EcomConfigs = {
    [K in keyof EcomConfigsFunctions]: UnwrapResult<ReturnType<EcomConfigsFunctions[K]>>;
};

const getEcomConfigsResolver =
    (locale: Locale) =>
    <K extends EcomConfigKeys>(key: K): EcomConfigsFunctions[K] =>
        getEcomConfigs(locale)[key];

export const getCachedEcomConfigByKey = async <K extends EcomConfigKeys>(
    locale: Locale,
    key: K,
): Promise<Result<EcomConfigs[K]>> => {
    const ecomConfigResult = await getEcomConfigsResolver(locale)(key)?.();
    const ecomConfig = unwrapResultSuccess(ecomConfigResult as Result<EcomConfigs[K]>);
    return ecomConfig !== undefined ? ResultSuccess(ecomConfig) : ResultEmpty();
};

export const getMultiCachedEcomConfigsByKeys = async <K extends EcomConfigKeys>(
    locale: Locale,
    keys: K[],
): Promise<{ [P in K]: Result<EcomConfigs[P]> }> => {
    const ecomConfigs = await Promise.all(
        keys.map(async (key) => {
            const result = await getCachedEcomConfigByKey(locale, key);
            return [key, result];
        }),
    );

    return Object.fromEntries(ecomConfigs);
};
